import { useEffect } from "react"
import { useSelector } from "react-redux"
import { TareasProvider, useTareas } from "./context/TareasContext"
import ViewSwitcher from "./components/ViewSwitcher"
import FilterBar from "./components/FilterBar"
import TaskCreateModal from "./components/TaskCreateModal"
import ProjectCreateModal from "./components/ProjectCreateModal"
import TaskDetailDrawer from "./components/TaskDetailDrawer"
import TablaView from "./views/TablaView"
import KanbanView from "./views/KanbanView"
import ListView from "./views/ListView"
import CalendarView from "./views/CalendarView"
import ProjectsView from "./views/ProjectsView"
import EditorJSPage from "./paginas/EditorJSPage"
import { FaTasks, FaPlus, FaInfoCircle, FaArrowLeft } from "react-icons/fa"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"

const Bone = ({ className = "" }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
)

const SkeletonLoader = ({ view }) => {
  if (view === "kanban") {
    return (
      <div className="flex gap-4 p-4 h-full overflow-hidden">
        {[1,2,3,4].map(col => (
          <div key={col} className="w-72 flex-shrink-0">
            <Bone className="h-10 mb-2 rounded-xl" />
            <div className="space-y-2">
              {[1,2,3].map(i => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-3 space-y-2">
                  <Bone className="h-3 w-16" />
                  <Bone className="h-4 w-full" />
                  <Bone className="h-3 w-2/3" />
                  <div className="flex gap-1 pt-1">
                    <Bone className="w-5 h-5 rounded-full" />
                    <Bone className="w-5 h-5 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (view === "calendario") {
    return (
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <Bone className="h-6 w-32" />
          <div className="flex gap-2"><Bone className="h-7 w-16" /><Bone className="h-7 w-16" /><Bone className="h-7 w-16" /></div>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <Bone key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  // Tabla / Lista
  return (
    <div className="p-4 space-y-2">
      <Bone className="h-9 w-full rounded-lg" />
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-2">
          <Bone className="w-4 h-4 rounded" />
          <Bone className="w-6 h-6 rounded-full" />
          <Bone className="h-4 flex-1 max-w-[200px]" />
          <Bone className="h-4 w-16" />
          <Bone className="h-4 w-20" />
          <Bone className="w-5 h-5 rounded-full" />
          <Bone className="h-4 w-24" />
        </div>
      ))}
    </div>
  )
}

const VIEW_COMPONENTS = {
  tabla:      TablaView,
  kanban:     KanbanView,
  lista:      ListView,
  calendario: CalendarView,
  paginas:    EditorJSPage,
}

const TareasAdminInner = () => {
  const user = useSelector((s) => s.auth.user)
  const {
    activeView,
    loading,
    tasks,
    fetchWorkers,
    fetchProjects,
    fetchEstadosTareas,
    setCreateModalOpen,
    setCreateProjectModalOpen,
    closeDetailDrawer,
    detailDrawerOpen,
    createModalOpen,
    selectedProject,
    setSelectedProject,
    filters,
    applyFilters,
  } = useTareas()

  useEffect(() => {
    fetchWorkers()
    fetchProjects()
  }, [user?.id, fetchWorkers, fetchProjects])

  useEffect(() => {
    if (selectedProject?.id) {
      fetchEstadosTareas(selectedProject.id)
      applyFilters({ ...filters, projectTagIds: [] })
    } else {
      fetchEstadosTareas(null)
      if (filters.projectTagIds?.length) {
        applyFilters({ ...filters, projectTagIds: [] })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTasks/fetchEstadosTareas cambian con estadosTareas y causarían bucle
  }, [selectedProject?.id])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable
      if (e.key === "Escape") {
        if (detailDrawerOpen) closeDetailDrawer()
      }
      if (isInput) return
      if (e.key === "n" && !e.metaKey && !e.ctrlKey && selectedProject) {
        e.preventDefault()
        setCreateModalOpen(true)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [detailDrawerOpen, createModalOpen, closeDetailDrawer, setCreateModalOpen, selectedProject])

  const ActiveView = VIEW_COMPONENTS[activeView] || TablaView
  const showProjectsView = !selectedProject

  return (
    <div className="w-full flex flex-col h-screen bg-white">
      
      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          <div className="flex items-center gap-4 py-4">
            {selectedProject && (
              <IconButton
                size="small"
                onClick={() => setSelectedProject(null)}
                sx={{ color: "#6B7280" }}
                title="Volver a proyectos"
              >
                <FaArrowLeft />
              </IconButton>
            )}
            <FaTasks className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">
              {selectedProject ? selectedProject.name : "Bloques de trabajo"}
            </h2>
          </div>

          <div className="flex flex-row gap-4 items-center justify-center">
            {showProjectsView ? (
              <Button
                onClick={() => setCreateProjectModalOpen(true)}
                variant="contained"
                size="small"
                startIcon={<FaPlus style={{ fontSize: 10 }} />}
                sx={{
                  fontFamily: "poppins",
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: "#ff6400",
                  fontSize: "12px",
                  paddingInline: "14px",
                  "&:hover": { backgroundColor: "#bb4a03" },
                }}
              >
                Nuevo proyecto
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <ViewSwitcher />
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  variant="contained"
                  size="small"
                  startIcon={<FaPlus style={{ fontSize: 10 }} />}
                  sx={{
                    fontFamily: "poppins",
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#ff6400",
                    fontSize: "12px",
                    paddingInline: "14px",
                    "&:hover": { backgroundColor: "#bb4a03" },
                  }}
                >
                  Nueva tarea
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros - solo cuando hay proyecto seleccionado y no es vista de páginas */}
      {!showProjectsView && activeView !== "paginas" && <FilterBar />}

      {/* Contenido */}
      <div className="flex-1 overflow-hidden relative">
        <style>{`
          @keyframes viewFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .view-animate { animation: viewFadeIn 0.2s ease-out; }
        `}</style>
        {showProjectsView ? (
          <div className="h-full view-animate"><ProjectsView /></div>
        ) : activeView === "paginas" ? (
          <div className="h-full view-animate"><EditorJSPage /></div>
        ) : loading ? (
          <SkeletonLoader view={activeView} />
        ) : tasks.length === 0 && activeView !== "calendario" ? (
          <div className="flex items-center justify-center h-full view-animate">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                <FaInfoCircle className="text-2xl text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">No hay tareas en este proyecto</p>
              <p className="text-xs text-gray-400">Crea una nueva tarea para comenzar · Presiona <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">N</kbd> para crear</p>
              <Button
                onClick={() => setCreateModalOpen(true)}
                variant="outlined"
                size="small"
                sx={{
                  fontFamily: "poppins",
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#ff6400",
                  color: "#ff6400",
                  fontSize: "12px",
                  mt: 1,
                  "&:hover": { borderColor: "#bb4a03", backgroundColor: "#fff7f0" },
                }}
              >
                Crear primera tarea
              </Button>
            </div>
          </div>
        ) : (
          <div key={activeView} className="h-full view-animate">
            <ActiveView />
          </div>
        )}
      </div>

      {/* Modals & Drawers */}
      <TaskCreateModal />
      <ProjectCreateModal />
      <TaskDetailDrawer />
    </div>
  )
}

const TareasAdminPage = () => (
  <TareasProvider>
    <TareasAdminInner />
  </TareasProvider>
)

export default TareasAdminPage
